import React, { useState } from 'react';
import { Mail, Building, User, MessageSquare, Loader, CheckCircle, AlertCircle, X } from 'lucide-react';
import './ContactForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({
    type: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.organization.trim()) {
      newErrors.organization = 'Organization/University is required';
    } else if (formData.organization.trim().length < 2) {
      newErrors.organization = 'Organization name must be at least 2 characters';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you! We\'ll be in touch soon to discuss pilot access for your program.'
        });
        setFormData({
          name: '',
          email: '',
          organization: '',
          message: ''
        });
        setErrors({});
      } else {
        setStatus({
          type: 'error',
          message: data.detail || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Unable to send message. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => {
    setStatus({ type: '', message: '' });
  };

  return (
    <div className="contact-form">
      {status.message && (
        <div className={`alert-banner ${status.type}`}>
          <div className="alert-content">
            <div className="alert-icon">
              {status.type === 'success' ? (
                <CheckCircle size={24} />
              ) : (
                <AlertCircle size={24} />
              )}
            </div>
            <div className="alert-message">
              {status.message}
            </div>
            <button className="alert-close" onClick={closeAlert} aria-label="Close alert">
              <X size={20} />
            </button>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            <User size={18} />
            <span>Full Name <span className="required">*</span></span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`form-input ${errors.name ? 'input-error' : ''}`}
            placeholder="John Doe"
            disabled={isSubmitting}
            aria-required="true"
            aria-invalid={errors.name ? 'true' : 'false'}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">
            <Mail size={18} />
            <span>Email Address <span className="required">*</span></span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-input ${errors.email ? 'input-error' : ''}`}
            placeholder="john.doe@university.edu"
            disabled={isSubmitting}
            aria-required="true"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="organization" className="form-label">
            <Building size={18} />
            <span>Organization / University <span className="required">*</span></span>
          </label>
          <input
            type="text"
            id="organization"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            className={`form-input ${errors.organization ? 'input-error' : ''}`}
            placeholder="Stanford Graduate School of Business"
            disabled={isSubmitting}
            aria-required="true"
            aria-invalid={errors.organization ? 'true' : 'false'}
          />
          {errors.organization && <span className="error-message">{errors.organization}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="message" className="form-label">
            <MessageSquare size={18} />
            <span>Message <span className="required">*</span></span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className={`form-textarea ${errors.message ? 'input-error' : ''}`}
            placeholder="Tell us about your MBA program and how you'd like to use Praxis AI..."
            rows="5"
            disabled={isSubmitting}
            aria-required="true"
            aria-invalid={errors.message ? 'true' : 'false'}
          />
          {errors.message && <span className="error-message">{errors.message}</span>}
        </div>

        <button
          type="submit"
          className="form-submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader size={18} className="spinner" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Mail size={18} />
              <span>Request Pilot Access</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
